import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AppRoute } from "@/App";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { luckyCardRepository } from "@/services/luckyCardReposytory";
import { LuckyCard, LuckyCardStatus, mapLuckyCardDtoToDomain } from "./types/LuckyCard";
import { CardDomain } from "@/Cards";
import { millisecondsToTime } from "@/utils/iso8601ToDate";
import { getMillisecondsUntilReset } from "@/utils/timerUtils";
import { QuestionStatistics } from "@/types/QuestionStatistics";
import { CONFIG } from "@/config";
import { selectLuckyCardLog } from "@/firebase/events";

export interface LuckyCardScreenViewModel {
  resetDate: string | null;
  deck: Array<LuckyCard | null>;
  luckyCard: LuckyCard | null;
  isLoading: boolean;
  isSubscribed: boolean | null;
  onSelectCard: (index: number) => void;
  onSubscriptionClick: () => void;
  onTelegramClick: () => void;
  navigateToHome: () => void;
  statistics: QuestionStatistics | null;
}

export const useLuckyCardScreenViewModel = (): LuckyCardScreenViewModel => {
  const [selectedCard, setSelectedCard] = useState<CardDomain | null>(null);
  const [luckyCard, setLuckyCard] = useState<CardDomain | null>(null);
  const [resetDate, setResetDate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { subscription, isLoading: subscriptionLoading } = useSubscription();
  const { getLuckyCards, chooseCard, getStatistics } = luckyCardRepository;
  const [cardDeck, setCardDeck] = useState<Array<LuckyCard | null>>([]);
  const [statistics, setStatistics] = useState<QuestionStatistics | null>(null);  
  
  const updateStatistics = async () => {  
    try {
      const response = await getStatistics();
      setStatistics(response);
    } catch (error) {
      console.error("Error updating statistics", error);
    }
  };

  const navigateToHome = () => {
    navigate(AppRoute.Home);
  };

  const onSubscriptionClick = () => {
    navigate(AppRoute.Subscription);
  };

  const onTelegramClick = () => {
    window.open(CONFIG.links.telegramAstrolog, "_blank");
  };

  const onSelectCard = async (index: number) => {
    try {
      if (selectedCard) return;
      await chooseCard(index);
      await selectLuckyCardLog(subscription?.isSubscribed || false);
      updateUIInfo();
    } catch (error) {
      console.error("Error updating deck", error);
    }
  };

  const updateDeck = async (
    deck: CardDomain[]|null,
    luckyCard: CardDomain | null,
    selectedCard: CardDomain | null,
    isThreeDayCards: boolean
  ) => {
    if (deck && deck.length > 0) {
      const newDeck = deck.map((card) => {
        const isLucky = card?.id === luckyCard?.id;
        const isSelected = selectedCard?.id === card?.id;
        const isGuessed = card?.id === luckyCard?.id && selectedCard?.id === card?.id;

        if (!isThreeDayCards && !isLucky && !isSelected) return null;

        let luckyCardStatus: LuckyCardStatus | null = null; 
        if(isGuessed) {
          luckyCardStatus = LuckyCardStatus.Guessed;
        } else if (isLucky) {
          luckyCardStatus = LuckyCardStatus.Lucky;
        } else if (isSelected) {
          luckyCardStatus = LuckyCardStatus.Selected;
        } else {
          luckyCardStatus = null;
        }

        return mapLuckyCardDtoToDomain(card, luckyCardStatus);
      });

      setCardDeck(newDeck);
    } else {
      setCardDeck(Array(12).fill(null));
    }
  };

  const getIsThreeDayCards = (resetDate: Date | null) => {
    if (!resetDate) return false;
    const threeDayCards = 4 * 24 * 60 * 60 * 1000;
    return getMillisecondsUntilReset(resetDate) > threeDayCards;
  };

  const getResetDate = (resetDate: Date | null) => {
    if (!resetDate) return null;
    const timeToReset = getMillisecondsUntilReset(resetDate);
    if (timeToReset > 0) {
      const { days, hours, minutes } = millisecondsToTime(timeToReset);
      return `${days} д. ${hours} ч. ${minutes} мин.`;
    }
    return null;
  };

  const updateUIInfo = async () => {
    try {
      const data = await getLuckyCards();
      const isThreeDayCards = getIsThreeDayCards(data?.resetDate);

      const luckyCardData = data.luckyCard;
      const selectedCardData = data.selectedCard;
      if (luckyCardData) {
        setLuckyCard(luckyCardData);
      }
      if (selectedCardData) {
        setSelectedCard(selectedCardData);
      }
      updateDeck(data.deck, luckyCardData, selectedCardData, !isThreeDayCards);
      const resetDate = getResetDate(data.resetDate);
      setResetDate(resetDate);
    } catch (error) {
      console.error("Error updating UI info", error);
    }
  };

  const initUI = async () => {
    // Ждем загрузки подписки
    if (subscriptionLoading) {
      return;
    }
    
    const isSubscribedValue = subscription?.isSubscribed ?? false;
    if(isSubscribedValue) {
      await updateUIInfo();
    } else {
      await updateStatistics();
    }
    setIsLoading(false);
  }

  useEffect(() => {
    initUI();
  }, [subscription, subscriptionLoading]);

  return {
    resetDate,
    deck: cardDeck,
    luckyCard: luckyCard ? mapLuckyCardDtoToDomain(luckyCard) : null,
    isLoading,
    onSelectCard,
    navigateToHome,
    onSubscriptionClick,
    onTelegramClick,
    isSubscribed: subscription?.isSubscribed ?? null,
    statistics,
  };
};
